import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Box, Container, List, ListItem, ListItemText, Typography, Checkbox, TextField, Tabs, Tab, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { TableChart, ViewModule } from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {createItemType, getItemTypes, getKB} from "../API/KB/KBAPI.js";
import { fetchItems, deleteItems, searchVectorDBItems } from "../API/items/itemsAPI";
import { colorText } from "../App/theme.js";
import LocalLoader from "../Loaders/LocalLoader.js";
import {deepClone, getQueryParamValue} from "../App/utils.js";
import { EmbeddingModels } from "../constants";
import { KB_TABS } from "../App/constants";
import { PaperWrapper } from "../Components/PaperWrapper";
import ItemsTable from "./ItemsTable";
import ItemsCards from "./ItemsCards";
import SetupItems from "./setup";
import HeaderButtons from "./HeaderButtons";
import {db, initDB} from "../Engines/Dexie";
import { AlertContext } from "../App/App";
import { BaseTools } from "./BaseTools";
import {IndexedDBTools} from "./IndexedDBTools";
import {VectorDBTools} from "./VectorDBTools";
import {createEmbedding} from "../API/OpenAIAPI";
import {convertIndexedItem, convertVectorDBItem} from "./utils";
import {debounce} from "../utils";
import {spaLogger} from "../API/APP/SPALoggerAPI";
import {useConfirm} from "../Components/ConfirmContext";


const ItemList = ({ kbTitle, activeTab, kbIdAsSubdomain, setKBData, KBData, setImportOpen, renderSettings }) => {
    const [selectedItems, setSelectedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [initDBIsLoading, setInitDBIsLoading] = useState(false);
    const navigate = useNavigate();
    let kbId = useParams().kbId;
    if (kbIdAsSubdomain) kbId = kbIdAsSubdomain;
    const [selectAll, setSelectAll] = useState(false);
    const [selectMode, setSelectMode] = useState(false);
    const [devMode, setDevMode] = useState(true);
    const [items, setItems] = useState(null);
    const [itemsIndexedDB, setItemsIndexedDB] = useState(null);
    const [itemsVectorDB, setItemsVectorDB] = useState(null);
    const [embeddingModel, setEmbeddingModel] = useState(null);
    const [embeddingDimension, setEmbeddingDimension] = useState(null);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
    const [exclusiveStartKey, setExclusiveStartKey] = useState(null);
    const [exclusiveStartKeyHistory, setExclusiveStartKeyHistory] = useState([]);
    const { setSystemAlert } = useContext(AlertContext);

    const [store, setStore] = useState(null);
    const [stores, setStores] = useState(null);
    const [index, setIndex] = useState(null);
    const [from2, setFrom2] = useState(undefined);
    const [to2, setTo2] = useState(undefined);

    const [from, setFrom] = useState(undefined);
    const [to, setTo] = useState(undefined);
    const [field, setField] = useState('itemId');

    const location = useLocation();
    const initialEngine = getQueryParamValue('engine') || 'NoSQL';
    const [engine, setEngine] = useState(initialEngine);

    // View mode state - default to cards view
    const [viewMode, setViewMode] = useState(() => {
        const savedViewMode = localStorage.getItem('itemsViewMode');
        return savedViewMode || 'cards';
    });

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        params.set('engine', engine);
        navigate({ search: params.toString() }, { replace: true });
    }, [engine, navigate, location.search]);

    const [limit, setLimit] = useState(() => {
        const savedLimit = localStorage.getItem('itemsLimit');
        return savedLimit !== null ? JSON.parse(savedLimit) : 25;
    });

    useEffect(() => {
        localStorage.setItem('itemsLimit', JSON.stringify(limit));
    }, [limit]);

    useEffect(() => {
        localStorage.setItem('itemsViewMode', viewMode);
    }, [viewMode]);


    useEffect(() => {
        setTo2('')
        setFrom2('')
        setTo('')
        setFrom('')
    }, [index, store, field]);

    // Clear items when switching tabs
    useEffect(() => {
        setItems(null);
        setItemsIndexedDB(null);
        setItemsVectorDB(null);
    }, [engine]);


    useEffect(() => {
        if (!KBData?.kbId) return;

        if (KBData?.walletPrivateKey) {
            if (engine === 'IndexedDB') {
                if (stores) return
                initDB({ KBData, setSystemAlert, setLoading: setInitDBIsLoading, showConfirm }).then(async (db) => {
                    // fetch items from search engine
                    const storesArray = await Promise.all(db.tables.map(async table => ({
                        name: table.name,
                        indexes: table.schema.indexes.map(index => index.name),
                        total: await table.count()
                    })));

                    if (storesArray?.length) {
                        setStores(storesArray)
                        if (!store && storesArray?.[0]?.name) setStore(storesArray[0].name) // set default first store
                        if (!index && storesArray?.[0]?.indexes?.[0]) setIndex(storesArray[0].indexes[0]) // set default first index
                    } else {
                        setStores([])
                    }

                });
            } else if (engine !== 'VectorDB') {
                // Don't auto-fetch for VectorDB - it only shows search results
                FetchItems()
            }
        }
    }, [KBData, engine, store, stores]);


    // IndexedDB fetch
    useEffect(() => {
        if (!store || !db) return;

        const isNumeric = !isNaN(parseFloat(from2));
        let fromValue = isNumeric ? parseFloat(from2) : from2;
        let toValue = isNumeric ? parseFloat(to2) : to2;


        if (typeof fromValue === 'number' && typeof toValue === 'number' && fromValue > toValue) return;

        FetchItems();

    }, [store, stores, from2, to2, index, limit]);

    const showConfirm = useConfirm();

    const FetchItems = async () => {
        if (!KBData?.walletPrivateKey) return;
        if (engine === 'IndexedDB' && (!store || !db)) return
        if (engine === 'VectorDB') return; // VectorDB only uses search, no fetch

        setIsLoading(true);

        try {
            if (engine === 'IndexedDB') {

                let isNumeric = typeof from2 === 'number';

                let fromValue = isNumeric ? parseFloat(from2) : from2;
                let toValue = isNumeric ? parseFloat(to2) : to2;

                if (isNumeric && isNaN(fromValue) || fromValue === '') fromValue = undefined;
                if (isNumeric && isNaN(toValue)  || toValue === '' ) toValue = undefined;

                let query;

                if (index === 'itemId') isNumeric = false;

                if (isNumeric) {
                    if (fromValue !== undefined && toValue !== undefined) {
                        query = db[store]?.where(index).between(fromValue, toValue); // inclusive range
                    } else if (fromValue !== undefined) {
                        query = db[store]?.where(index).aboveOrEqual(fromValue);
                    } else if (toValue !== undefined) {
                        query = db[store]?.where(index).belowOrEqual(toValue);
                    } else {
                        query = db[store]; // No range specified, use the base store query
                    }
                } else {
                    if (fromValue !== undefined) {
                        query = db[store]?.where(index).startsWith(fromValue.toString())
                    } else {
                        query = db[store];
                    }
                }

                query
                    .limit(limit)
                    .toArray()
                    .then((items) => {
                        setItemsIndexedDB(items.map(item => convertIndexedItem(item, store)));
                    });
            } else {
                const params = { limit, exclusiveStartKey, from, to, field };
                // strip embedding from the output
                if (field !== 'updatedAt') params.filterMeta = ['embedding'];

                const itemsData = await fetchItems(deepClone(KBData), params);
                // Check for items with non-existing itemTypes
                if (showConfirm) {
                    const items = itemsData?.items
                    const existingItemTypes = KBData?.itemTypes || {};
                    const newItemTypes = {};

                    items.forEach(item => {
                        const itemType = item.meta.itemType;
                        if (!existingItemTypes[itemType]) {
                            // Generate attributes with label and placeholder
                            const attributes = item.meta.attributes.map(attr => ({
                                ...attr,
                                label: attr.attrName.charAt(0).toUpperCase() + attr.attrName.slice(1),
                                placeholder: `Enter ${attr.attrName}`
                            }));

                            newItemTypes[itemType] = {
                                embeddingTemplate: '',
                                attributes
                            };
                        }
                    });

                    for (const [itemType, data] of Object.entries(newItemTypes)) {
                        const confirmation = await showConfirm({
                            title: 'Create Item Type',
                            content: `You have items with a non-existing type "${itemType}". Do you want to create it?`
                        });

                        if (confirmation) {
                            // Double-check if the itemType still doesn't exist
                            const currentItemTypes = await getItemTypes();
                            if (!currentItemTypes[itemType]) {
                                await createItemType(itemType, data.attributes, data.embeddingTemplate);
                            }
                        }
                    }
                }

                if (itemsData?.items) {
                    setItems(itemsData.items);
                    setLastEvaluatedKey(itemsData.lastEvaluatedKey);
                }
            }
        } catch (error) {
            console.error('Error fetching items:', error);
        }
        setIsLoading(false);
    }

    const fetchKB = async () => {
        const defaultEmbeddingModelKey = Object.keys(EmbeddingModels).find(key => EmbeddingModels[key].default);
        const KB = await getKB();
        setKBData(KB);
        if (!KB?.embeddingModel) {
            setEmbeddingModel(defaultEmbeddingModelKey);
            setEmbeddingDimension(EmbeddingModels[defaultEmbeddingModelKey]?.dimension);
        }
        if (KB?.searchEngine === 'VectorDB') {
            // setEngine('VectorDB')
        }
    }

    useEffect(() => {
        if (
            embeddingModel && embeddingModel !== 'none' &&
            (embeddingDimension === 'none' || embeddingDimension > EmbeddingModels[embeddingModel].dimension)
        ) {
            setEmbeddingDimension(EmbeddingModels[embeddingModel].dimension);
        }
    }, [embeddingModel, embeddingDimension]);

    useEffect(() => {
        if (engine === 'VectorDB') return; // VectorDB doesn't use pagination

        if (exclusiveStartKey &&
            (field === 'updatedAt' && !exclusiveStartKey?.updatedAt || field === 'itemId' && exclusiveStartKey?.updatedAt)) {
            setExclusiveStartKey(null)
            return;
        }
        if (KBData?.walletPrivateKey) {
            FetchItems();
        }
    }, [KBData, exclusiveStartKey, field, limit, engine]);

    useEffect(() => {
        fetchKB();
    }, []);

    const handleSearch = async (query) => {
        if (query.trim() === "") {
            if (engine === 'VectorDB') {
                // For VectorDB, clear results when query is empty
                setItemsVectorDB([]);
            } else {
                FetchItems();
            }
        } else {
            try {
                setIsLoading(true);

                if (engine === 'VectorDB') {
                    // VectorDB search
                    try {
                        const topK = KBData?.options?.vectorDBTopK || 30;
                        const maxTokens = KBData?.options?.vectorDBMaxTokens || 10000;
                        const minScore = KBData?.options?.vectorDBMinScore || null;
                        const results = await searchVectorDBItems(KBData, query, topK, maxTokens, minScore);
                        const convertedItems = (results?.items || []).map(item => convertVectorDBItem(item));
                        setItemsVectorDB(convertedItems);
                    } catch (e) {
                        spaLogger('Failed to search VectorDB')
                        console.log(e)
                        setSystemAlert({ severity: 'error', message: 'VectorDB search failed' });
                    }
                } else if (engine === 'IndexedDB') {
                    // IndexedDB search
                    try {
                        const res = await createEmbedding(query, KBData?.embeddingModel, KBData);
                        const inputVector = res.data[0]?.embedding.slice(0, (KBData?.embeddingDimension - 1))
                        const knnResponse = await db.knn({inputVector, maxItems: limit, renderSettings})
                        setItemsIndexedDB(knnResponse)
                    } catch (e) {
                        spaLogger('Failed to create embeddings')
                        console.log(e)
                    }
                }

                setIsLoading(false);
            } catch (error) {
                console.error("Error searching items:", error);
            }
        }
    };

    const handleSearchOnEnter = async (e) => {
        if (e.key === "Enter") {
            if ( typeof debouncedHandleSearch.cancel === 'function') debouncedHandleSearch.cancel();
            handleSearch(e.target.value);
        }
    };

    const debouncedHandleSearch = useCallback(debounce(handleSearch, 500), [KBData, limit]);

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedItems([]);
        } else {
            setSelectedItems(items.map((item) => item.meta.itemId));
        }
        setSelectAll(!selectAll);
    };

    useEffect(() => {
        setExclusiveStartKeyHistory([])
        setLastEvaluatedKey(null);
    }, [field]);

    useEffect(() => {
        setSelectAll(false);
        setSelectedItems([]);
    }, [items]);

    const handleDeleteSelected = async () => {
        const confirmation = await showConfirm({
            title: 'Delete Items Confirmation',
            content: `Are you sure you want to delete the selected items?`
        })

        if (confirmation) {
            try {
                await deleteItems(selectedItems, KBData);
                if (items) setItems(items.filter(item => !selectedItems.includes(item.meta.itemId)));
                if (itemsVectorDB) setItemsVectorDB(itemsVectorDB.filter(item => !selectedItems.includes(item.meta.itemId)));
                setSelectedItems([]);
            } catch (error) {
                if (error?.response?.data?.error) alert(error.response.data.error);
                console.error('Error deleting selected items:', error);
            }
        }
        if (engine !== 'VectorDB') {
            FetchItems(); // VectorDB doesn't auto-refresh
        }
    };

    const handleLimitChange = (event) => {
        setLimit(parseInt(event.target.value));
    };

    const handleNextPage = () => {
        setExclusiveStartKeyHistory([...exclusiveStartKeyHistory, exclusiveStartKey]);
        setExclusiveStartKey(lastEvaluatedKey);
    };

    const handlePreviousPage = () => {
        if (exclusiveStartKeyHistory.length > 0) {
            const previousStartKey = exclusiveStartKeyHistory[exclusiveStartKeyHistory.length - 1];
            setExclusiveStartKeyHistory(exclusiveStartKeyHistory.slice(0, -1));
            setExclusiveStartKey(previousStartKey);
        }
    };

    const handleTabChange = (event, newValue) => {
        setEngine(newValue);
    };
    
    const handleViewModeChange = (event, newViewMode) => {
        if (newViewMode !== null) {
            setViewMode(newViewMode);
        }
    };

    const embeddingConfigured = (KBData?.embeddingModel && KBData?.embeddingDimension);

    if (!kbTitle) return null;

    const itemTypes = KBData?.itemTypes ? Object.keys(KBData?.itemTypes) : [];

    const isIndexedDB = engine === 'IndexedDB';
    const isVectorDB = engine === 'VectorDB';

    let isVisible = embeddingConfigured && !!items?.length || from || to || isIndexedDB || isVectorDB;

    return (
        <Container style={{ padding: 0 }}>
            {kbId && embeddingConfigured && activeTab === KB_TABS.items && <HeaderButtons
                setImportOpen={setImportOpen}
                {...{ itemTypes, embeddingConfigured, fetchKB, setIsLoading }} />}

            <LocalLoader open={isLoading || initDBIsLoading} />

            <Box mt={3}>
                <List>
                    <Box mt={0} display="flex" justifyContent="center">
                        <Typography component="h2" variant="h5" align="center" style={{
                            color: colorText,
                            marginBottom: '32px'
                        }} gutterBottom>
                            {!isIndexedDB && !isVectorDB && embeddingConfigured && items && items.length > 0
                                ? `${items && items.length} Item${items.length > 1 ? 's' : ''}`
                                : (embeddingConfigured ? '' : '')}

                            {isIndexedDB && embeddingConfigured && itemsIndexedDB && itemsIndexedDB.length > 0
                                ? `${itemsIndexedDB && itemsIndexedDB.length} Item${itemsIndexedDB.length > 1 ? 's' : ''}`
                                : (embeddingConfigured ? '' : '')}

                            {isVectorDB && embeddingConfigured && itemsVectorDB && itemsVectorDB.length > 0
                                ? `${itemsVectorDB && itemsVectorDB.length} Item${itemsVectorDB.length > 1 ? 's' : ''}`
                                : (embeddingConfigured ? '' : '')}

                        </Typography>
                    </Box>

                    {isVisible && KBData?.searchEngine && <Tabs
                        value={engine}
                        onChange={handleTabChange}
                        aria-label="item view tabs"
                        sx={{mb: 1}}
                    >
                        <Tab value="NoSQL" label={<span style={{ textTransform: 'none' }}>NoSQL</span>} />
                        <Tab value={KBData?.searchEngine} label={<span style={{ textTransform: 'none' }}>{KBData?.searchEngine}</span>} />
                    </Tabs>}
                    
                    {/* View Mode Toggle */}
                    {isVisible && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={handleViewModeChange}
                                aria-label="view mode"
                                size="small"
                            >
                                <ToggleButton value="table" aria-label="table view">
                                    <TableChart fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="cards" aria-label="cards view">
                                    <ViewModule fontSize="small" />
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    )}

                    {KBData?.model && !embeddingConfigured && <SetupItems {...{ EmbeddingModels, setIsLoading, embeddingModel, embeddingDimension, setEmbeddingDimension, fetchKB, setEmbeddingModel }} />}

                    {isVisible && <PaperWrapper>

                        {isVisible && engine !== KBData?.searchEngine && <BaseTools {...{
                            setSelectMode, embeddingConfigured, setDevMode, selectedItems, items, handleDeleteSelected,
                            exclusiveStartKeyHistory, handleLimitChange, handleNextPage, handlePreviousPage, selectMode,
                            devMode, limit, lastEvaluatedKey, FetchItems, setFrom, setTo, setField, field, from, to,
                            KBData, setSystemAlert, setLoading: setInitDBIsLoading }} />}

                        {isVisible && engine === 'IndexedDB' && <IndexedDBTools {...{
                            setSelectMode, embeddingConfigured, setDevMode, selectedItems, items, handleDeleteSelected,
                            exclusiveStartKeyHistory, handleLimitChange, handleNextPage, handlePreviousPage, selectMode,
                            devMode, limit, lastEvaluatedKey, FetchItems, setFrom: setFrom2, setTo: setTo2, setField,
                            field, from: from2, to: to2, debouncedHandleSearch, KBData, handleSearchOnEnter, stores, store,
                            setStore, index, setIndex, itemsIndexedDB, setSystemAlert, setLoading: setInitDBIsLoading
                        }} />}

                        {isVisible && engine === 'VectorDB' && <VectorDBTools {...{
                            setSelectMode, embeddingConfigured, setDevMode, selectedItems, items, handleDeleteSelected,
                            exclusiveStartKeyHistory, handleLimitChange, handleNextPage, handlePreviousPage, selectMode,
                            devMode, limit, lastEvaluatedKey, FetchItems, setFrom, setTo, setField,
                            field, from, to, debouncedHandleSearch, KBData, handleSearchOnEnter,
                            itemsVectorDB, setSystemAlert, setLoading: setInitDBIsLoading
                        }} />}

                        {selectMode && !!items?.length && (
                            <ListItem>
                                <Checkbox
                                    edge="start"
                                    checked={selectAll}
                                    tabIndex={-1}
                                    disableRipple
                                    onChange={handleSelectAll}
                                />
                                <ListItemText sx={{ color: colorText }} onClick={handleSelectAll}
                                              primary={<span style={{fontSize: 14}}>Select All</span>} />
                            </ListItem>
                        )}

                        {viewMode === 'table' ? (
                            <ItemsTable
                                itemsIndexedDB={engine === 'IndexedDB' ? itemsIndexedDB : undefined}
                                itemsVectorDB={engine === 'VectorDB' ? itemsVectorDB : undefined}
                                {...{embeddingConfigured, items, selectMode, selectedItems, KBData, setItems,
                                setSelectedItems, devMode, kbId, limit, setLimit}} />
                        ) : (
                            <ItemsCards
                                itemsIndexedDB={engine === 'IndexedDB' ? itemsIndexedDB : undefined}
                                itemsVectorDB={engine === 'VectorDB' ? itemsVectorDB : undefined}
                                {...{embeddingConfigured, items, selectMode, selectedItems, KBData, setItems,
                                setSelectedItems, devMode, kbId, limit, setLimit}} />
                        )}

                    </PaperWrapper>}

                </List>
            </Box>
        </Container>
    );
}

export default ItemList;